import { GameMeta } from "./types";

export const STARTING_ELO = 1000;
export const K_FACTOR = 32;

export interface EloTier {
  name: string;
  minRating: number;
  color: string;
}

export const ELO_TIERS: EloTier[] = [
  { name: "Master", minRating: 1800, color: "red.400" },
  { name: "Diamond", minRating: 1600, color: "purple.400" },
  { name: "Platinum", minRating: 1400, color: "cyan.400" },
  { name: "Gold", minRating: 1200, color: "yellow.400" },
  { name: "Silver", minRating: 1000, color: "gray.300" },
  { name: "Bronze", minRating: 800, color: "orange.400" },
  { name: "Iron", minRating: 0, color: "gray.500" },
];

export function getEloTier(rating: number): EloTier {
  for (const tier of ELO_TIERS) {
    if (rating >= tier.minRating) return tier;
  }
  return ELO_TIERS[ELO_TIERS.length - 1];
}

export function isRankedGame(meta: GameMeta): boolean {
  return (meta.moveTimeoutSeconds ?? 0) > 0 && meta.betAmount > 0;
}

interface CompletedGame {
  creator: string;
  opponent?: string;
  winner?: string;
  createdAt: number;
}

/**
 * Compute Elo rating for a pubkey from a chronologically sorted list of ranked games.
 * Standard Elo: E = 1/(1+10^((Rb-Ra)/400)), R' = R + K*(S-E)
 */
export function computeElo(pubkey: string, rankedGames: CompletedGame[]): number {
  const ratings = new Map<string, number>();
  const getRating = (pk: string) => ratings.get(pk) ?? STARTING_ELO;

  for (const game of rankedGames) {
    if (!game.opponent) continue;

    const isCreator = game.creator === pubkey;
    const isOpponent = game.opponent === pubkey;
    if (!isCreator && !isOpponent) continue;

    const opponentPk = isCreator ? game.opponent : game.creator;
    const myRating = getRating(pubkey);
    const oppRating = getRating(opponentPk);

    const expected = 1 / (1 + Math.pow(10, (oppRating - myRating) / 400));

    let score: number;
    if (game.winner === pubkey) {
      score = 1;
    } else if (game.winner && game.winner !== pubkey) {
      score = 0;
    } else {
      score = 0.5; // draw
    }

    const newRating = Math.round(myRating + K_FACTOR * (score - expected));
    ratings.set(pubkey, newRating);

    // Also update opponent rating to keep relative ratings consistent
    const oppExpected = 1 - expected;
    const oppScore = 1 - score;
    ratings.set(opponentPk, Math.round(oppRating + K_FACTOR * (oppScore - oppExpected)));
  }

  return getRating(pubkey);
}
